'use client';

import type { AnimationEvent, HTMLAttributes, MouseEvent, ReactNode, ReactPortal } from 'react';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { CloseIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { IconButton } from '../buttons';
import { Spinner } from '../spinner';
import { Tooltip } from '../tooltip';
import { Heading } from '../typography';

import styles from './modal.module.css';

let modalRoot: HTMLElement | null;

if (typeof window !== 'undefined') {
  modalRoot = document.getElementById('modal-root');

  if (modalRoot === null) {
    modalRoot = document.createElement('div');
    modalRoot.id = 'modal-root';
    document.body.appendChild(modalRoot);
  }
}

const MODAL_OPEN_CLASS = 'modal-open';

export type ModalPortalProps = {
  isOpen?: boolean;
  onClose?: (event: MouseEvent<HTMLElement>) => void;
} & HTMLAttributes<HTMLDivElement>;

/**
 * @param {boolean} [isOpen] Whether the modal is open or not.
 * @default false
 * @param {ReactNode} [children] The main content of the modal.
 * @default undefined
 * @param {MouseEvent<HTMLElement>} [onClose] Callback for when the modal is closed.
 * @default (undefined)
 */
export function ModalPortal({
  isOpen = false,
  children,
  onClose,
  className,
  ...props
}: ModalPortalProps): ReactPortal | null {
  const [shouldRender, setShouldRender] = useState(isOpen);

  const handleBackgroundClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose?.(event);
  };

  const onAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isOpen) setShouldRender(false);
  };

  useEffect(() => {
    if (isOpen) setShouldRender(true);

    typeof window !== 'undefined' && document.body.classList.toggle(MODAL_OPEN_CLASS, isOpen);
  }, [isOpen]);

  if (!modalRoot || !shouldRender) return null;

  return createPortal(
    <div
      role="dialog"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-content"
      className={classNames(className, styles.background, isOpen ? styles.enter : styles.exit)}
      onClick={handleBackgroundClick}
      onAnimationEnd={onAnimationEnd}
      {...props}
    >
      {children}
    </div>,
    modalRoot
  );
}

export type ModalProps = {
  title?: ReactNode;
  footer?: ReactNode;
  loading?: boolean;
  contentProps?: HTMLAttributes<HTMLDivElement>;
} & Omit<ModalPortalProps, 'title'>;

/**
 * @param {boolean} [isOpen] Whether the modal is open or not.
 * @default false
 * @param {boolean} [loading] Shows spinner over modal's content and disables closing functionality.
 * @default false
 * @param {ReactNode} [title] Title of the modal.
 * @default undefined
 * @param {ReactNode} [children] The main content of the modal.
 * @default undefined
 * @param {ReactNode} [footer] Content for the footer section of the modal.
 * @default undefined
 * @param {MouseEvent<HTMLElement>} [onClose] Callback for when the modal is closed.
 * @default undefined
 * @param {HTMLAttributes<HTMLDivElement>} [contentProps] Optional props to be applied to the modal's content.
 * @default undefined
 */
export function Modal({
  isOpen = false,
  loading = false,
  title,
  children,
  footer,
  onClose,
  contentProps,
  ...props
}: ModalProps) {
  const { t } = useTranslation();
  const [isMounted, setMounted] = useState(false);

  const handleClose = (event: MouseEvent<HTMLElement>) => {
    if (!loading) onClose?.(event);
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <ModalPortal isOpen={isOpen} onClose={handleClose} {...props}>
      <div {...contentProps} className={classNames(styles.modal, contentProps?.className, 'lspro-scrollbars')}>
        {(title || onClose) && (
          <header>
            {title && (
              <Heading id="dialog-title" data-testid="modal-title">
                {title}
              </Heading>
            )}

            {onClose && (
              <Tooltip label={t('modal.closeButton.label')} hidden={!isOpen}>
                <IconButton
                  data-testid="modal-close-button"
                  rounded
                  onClick={handleClose}
                  aria-label={t('modal.closeButton.label')}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            )}
          </header>
        )}

        <main id="dialog-content" data-testid="modal-content" className="lspro-scrollbars">
          {children}
        </main>

        {footer && <footer>{footer}</footer>}

        {loading && (
          <div className={styles.spinnerOverlay}>
            <Spinner size={40} />
          </div>
        )}
      </div>
    </ModalPortal>
  );
}
